import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { PersonIcon, ShieldLockIcon } from '@navikt/aksel-icons';
import { Routes } from '../../enums/Routes';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo';
import { getInitials } from '../../utils/getInitials';
import { TabbedPageLayout } from '../../components/TabbedPageLayout';
import { PasswordForm } from './components/PasswordForm';
import { PrivacyTab } from './components/PrivacyTab';
import { ProfileForm } from './components/ProfileForm';

const ProfileTab = () => {
  const { t } = useTranslation();
  const { data: userinfo, isLoading, error } = useGetUserinfo();

  if (isLoading) {
    return (
      <div aria-busy="true">
        <span className="ds-sr-only">{t('settings.profile.loading')}</span>
        <Skeleton variant="rectangle" height="360px" />
      </div>
    );
  }

  if (error || !userinfo) {
    return (
      <Alert data-color="danger">
        <Heading level={2} data-size="xs">
          {t('settings.profile.error.title')}
        </Heading>
        <Paragraph>{t('settings.profile.error.message')}</Paragraph>
      </Alert>
    );
  }

  return (
    <>
      <ProfileForm nickname={userinfo.nickname ?? ''} email={userinfo.email ?? ''} />
      {!userinfo.isSsoUser && <PasswordForm />}
    </>
  );
};

/** Innstillinger for brukeren, med samme oppsett som organisasjonssidene: profil og passord, og personvern */
export const SettingsPage = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { data: userinfo, isLoading } = useGetUserinfo();
  const name = userinfo?.nickname?.trim() || t('settings.title');

  return (
    <TabbedPageLayout
      initials={userinfo?.nickname ? getInitials(userinfo.nickname) : ''}
      eyebrow={t('settings.title')}
      title={name}
      isLoading={isLoading}
      tabsLabel={t('settings.tabsLabel')}
      tabs={[
        { key: 'profile', to: Routes.UserSettings, icon: PersonIcon, label: t('settings.tabs.profile') },
        { key: 'privacy', to: Routes.UserPrivacy, icon: ShieldLockIcon, label: t('settings.tabs.privacy') },
      ]}
    >
      {pathname.startsWith(Routes.UserPrivacy) ? <PrivacyTab /> : <ProfileTab />}
    </TabbedPageLayout>
  );
};
